import { BACKEND_URL, DEFAULT_COUNTRY_CODE } from '../../../utils/constants';
import { useEffect, useState } from 'react';
import {
  moneyHandleClick,
  pushCheckoutStep,
  pushDonationFormValidationErrors,
  pushGtmEvent,
  pushViewItem,
} from '../../../utils/gtm';

type FormState = {
  current_page: string;
  appeal_id: string;
  appeal_name: string;
  count_source_codes: number;
  selected: { [key: string]: string };
  default: { [key: string]: string };
  values: { [key: string]: string };
  errors: { [key: string]: string };
};

const CharityDonationFormStateListener = () => {
  const [prevFormState, setPrevFormState] = useState<FormState>();
  const [formState, setFormState] = useState<FormState>();

  useEffect(() => {
    const listenIframePostMessages = (e: MessageEvent) => {
      if (e.origin !== BACKEND_URL) {
        return;
      }

      if (e.data.type === 'DONATION_FORM_FAILED_SUBMISSION') {
        pushGtmEvent({
          event: 'donationFormSubmissionError',
          step: e.data.payment_gateway,
          current_page: e.data.current_page,
          error: e.data.error,
          ecommerce: {
            checkout: {
              actionField: {
                option: 'Modal',
              },
            },
          },
        });
      }

      if (e.data.type !== 'charity_donation_form_state' || !e.data.current_page) {
        return;
      }
      setFormState(e.data);
    };

    window.addEventListener('message', listenIframePostMessages);

    return () => {
      window.removeEventListener('message', listenIframePostMessages);
    };
  }, []);

  useEffect(() => {
    if (!formState) {
      return;
    }

    setPrevFormState(formState);

    const currentStep = formState.current_page as string;
    if (currentStep === 'money_handles') {
      pushViewItem([
        {
          item_id: formState.appeal_id,
          item_name: formState.appeal_name,
          item_category: 'Modal',
          item_variant: formState.default.donation_type,
        },
      ]);
    }

    // Push moneyHandleClick GTM event when Money_handles step is submitted.
    if (
      prevFormState &&
      prevFormState.current_page === 'money_handles' &&
      currentStep === 'name_and_email'
    ) {
      moneyHandleClick(
        formState.selected.money_handle_position,
        'Modal',
        formState.appeal_name,
        formState.selected.amount,
        formState.selected.donation_type,
        formState.selected.default_money_handle_position,
        'text_only',
      );
    }

    let step = null;
    switch (currentStep) {
      case 'name_and_email':
        step = { id: 1, name: 'amount' };
        break;
      case 'address_step':
        step = { id: 2, name: 'address' };
        break;

      case 'gift_aid_page':
        step = { id: 5, name: 'giftaid' };
        break;
      case 'payment_methods_step':
        step = { id: DEFAULT_COUNTRY_CODE === 'IE' ? 5 : 6, name: 'payment methods' };
        break;
      case 'payment_step':
        step = { id: DEFAULT_COUNTRY_CODE === 'IE' ? 6 : 7, name: 'payment' };
        break;
      default:
        break;
    }
    const validationErrors = formState.errors;
    if (step && validationErrors) {
      // Send GTM Validation errors event.
      const normalisedErrors = {} as { [key: string]: string };
      Object.keys(validationErrors).forEach((key) => {
        let newKey = key;
        switch (key) {
          case 'address][address_line1':
            newKey = 'addressLine1';
            break;
          case 'address][locality':
            newKey = 'locality';
            break;
          case 'address][postal_code':
            newKey = 'postalCode';
            break;
          default:
            break;
        }
        normalisedErrors[newKey] = validationErrors[key];
      });

      pushDonationFormValidationErrors(step.name, normalisedErrors);
      return;
    }

    if (
      currentStep === 'payment_methods_step' &&
      formState.selected.donation_type === 'single_donation'
    ) {
      pushGtmEvent({
        event: 'transaction_fee_element_visible',
        transaction_fee_element_type: 'dropdown',
      });
    }

    // Source code select field is visible only when there are more than 1 option is available.
    if (currentStep === 'address_step' && formState.count_source_codes > 1) {
      pushGtmEvent({
        event: 'custom.elementVisibility',
        elementId: 'source_code',
      });
    }

    if (step) {
      const products = [
        {
          name: formState.appeal_name,
          id: formState.appeal_id,
          price: formState.selected.amount,
          category: 'Modal',
          variant: formState.selected.donation_type,
          quantity: 1,
        },
      ];
      pushCheckoutStep(step.id, 'Modal', products);
    }
  }, [formState]);
  return null;
};

export default CharityDonationFormStateListener;
